import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Polyline, CircleMarker, Tooltip } from "react-leaflet";
import LeafletMap, { FitBounds } from "@/components/map/LeafletMap";
import { useTrailSave, TrailSaveProgress } from "@/lib/useTrailSave";
import BusinessLayers, { LAYER_META } from "@/components/map/BusinessLayers";
import HeatLayer from "@/components/map/HeatLayer";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Search, Route, Save, MapPin, DownloadCloud, RefreshCw, CheckCircle2, Ruler, Layers, X, Utensils, Bike } from "lucide-react";
import { toast } from "sonner";
import { US_STATES } from "./directory/states";
import { countiesFor } from "./directory/counties";

const NET_LABEL = { ncn: "National", rcn: "Regional", lcn: "Local" };
const SURFACE_CHIP = {
  Paved: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  Unpaved: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  Mixed: "bg-sky-500/15 text-sky-300 border-sky-500/30",
};

// Per-state filter memory
const FILTER_KEY = "velopaths_trail_filters";
const readPrefs = () => { try { return JSON.parse(localStorage.getItem(FILTER_KEY) || "{}"); } catch { return {}; } };

export default function TrailDiscoveryPanel() {
  const [stateOptions, setStateOptions] = useState([]);
  const [state, setState] = useState("");
  const [county, setCounty] = useState("all");
  const [q, setQ] = useState("");
  const [minMiles, setMinMiles] = useState("2");
  const [hideNumbered, setHideNumbered] = useState(true);
  const [surface, setSurface] = useState("all");
  const [pavedOnly, setPavedOnly] = useState(false);
  const [minRestaurants, setMinRestaurants] = useState("0");
  const [minBikeShops, setMinBikeShops] = useState("0");
  const [sortBy, setSortBy] = useState("length");
  const [recomputing, setRecomputing] = useState(false);
  const [trails, setTrails] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [selected, setSelected] = useState(null);   // full trail doc
  const [loadingTrail, setLoadingTrail] = useState(false);
  const [bufferMiles, setBufferMiles] = useState("1");
  const [job, setJob] = useState(null);
  const [harvesting, setHarvesting] = useState(false);
  const pollRef = useRef(null);
  const [businesses, setBusinesses] = useState([]);
  const [bizStatus, setBizStatus] = useState(null);
  const [bizLoading, setBizLoading] = useState(false);
  const [layers, setLayers] = useState({ restaurant: true, bike_shop: true, coffee: false, brewery: false, hotel: false });
  const [bikeFilter, setBikeFilter] = useState("all");
  const [heatOn, setHeatOn] = useState(false);
  const bizCacheRef = useRef({});

  const codeToName = useMemo(() => Object.fromEntries(US_STATES.map(([c, n]) => [c, n])), []);

  const loadStates = useCallback(async () => {
    try { const { data } = await api.get("/admin/trails/states"); setStateOptions(data.states); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }, []);

  const current = stateOptions.find((s) => s.state === state);
  const countyOpts = useMemo(() => countiesFor(state), [state]);

  const loadList = useCallback(async () => {
    if (!state) { setTrails([]); return; }
    setLoadingList(true);
    const t0 = performance.now();
    try {
      const surf = pavedOnly ? "paved" : surface;
      const cb = county && county !== "all" ? countiesFor(state).find((c) => c.name === county)?.bbox : null;
      const { data } = await api.get("/admin/trails", { params: { state, q: q.trim() || undefined, min_miles: Number(minMiles), hide_numbered: hideNumbered, surface: surf !== "all" ? surf : undefined, min_restaurants: Number(minRestaurants), min_bike_shops: Number(minBikeShops), sort: sortBy, bbox: cb ? cb.join(",") : undefined } });
      const ms = Math.round(performance.now() - t0);
      console.info(`[TrailDiscovery] search state=${state} county=${county} min=${minMiles}mi → found ${data.total}, returned ${data.items.length} (single page, limit 500) in ${ms}ms`);
      setTrails(data.items);
    } catch (e) {
      console.error("[TrailDiscovery] search failed", e);
      toast.error(apiError(e.response?.data?.detail));
    }
    finally { setLoadingList(false); }
  }, [state, county, q, minMiles, hideNumbered, surface, pavedOnly, minRestaurants, minBikeShops, sortBy]);

  const recompute = async () => {
    if (!state) return;
    setRecomputing(true);
    try {
      await api.post("/admin/trails/metrics/recompute", { state });
      toast.info("Recomputing CRM shop metrics in the background…");
      const iv = setInterval(async () => {
        try {
          const { data } = await api.get("/admin/trails/metrics/status", { params: { state } });
          if (["done", "error", "none"].includes(data.status)) {
            clearInterval(iv); setRecomputing(false);
            if (data.status === "done") { toast.success(`Metrics updated for ${data.counts?.trails_updated ?? 0} trails`); loadList(); }
            if (data.status === "error") toast.error(`Recompute failed: ${data.error || "unknown"}`);
          }
        } catch { /* keep polling */ }
      }, 3000);
    } catch (e) { setRecomputing(false); toast.error(apiError(e.response?.data?.detail)); }
  };

  const pollStatus = useCallback(async (st) => {
    try {
      const { data } = await api.get("/admin/trails/harvest/status", { params: { state: st } });
      setJob(data);
      if (["done", "error", "none", "cancelled"].includes(data.status)) {
        clearInterval(pollRef.current); pollRef.current = null; setHarvesting(false);
        if (data.status === "done") { toast.success(`Harvest complete — ${data.counts?.trails_upserted ?? 0} trails`); loadStates(); loadList(); }
        if (data.status === "error") toast.error(`Harvest failed: ${data.error || "unknown"}`);
      }
    } catch { /* keep polling */ }
  }, [loadStates, loadList]);

  const abortHarvest = async () => {
    try {
      await api.post("/admin/trails/harvest/cancel", { state: job?.state || state });
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      setHarvesting(false);
      setJob((j) => ({ ...(j || {}), status: "cancelled", phase: "cancelled" }));
      toast.success("Harvest aborted");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const harvest = async () => {
    if (!state) { toast.info("Select a state first"); return; }
    setHarvesting(true);
    try {
      const cb = county && county !== "all" ? countiesFor(state).find((c) => c.name === county)?.bbox : null;
      await api.post("/admin/trails/harvest", { state, min_import_miles: Number(minMiles) || 0, bbox: cb || null });
      toast.info(`Harvesting ${codeToName[state] || state}${cb ? ` · ${county} county` : ""}${Number(minMiles) ? ` (${minMiles}+ mi)` : ""}… runs in the background.`);
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(() => pollStatus(state), 3000);
      pollStatus(state);
    } catch (e) { setHarvesting(false); toast.error(apiError(e.response?.data?.detail)); }
  };

  useEffect(() => { loadStates(); }, [loadStates]);
  // Persist this admin's filter choices per state.
  useEffect(() => {
    if (!state) return;
    const all = readPrefs();
    all[state] = { minMiles, hideNumbered, surface, pavedOnly, minRestaurants, minBikeShops, sortBy };
    try { localStorage.setItem(FILTER_KEY, JSON.stringify(all)); } catch { /* ignore */ }
  }, [state, minMiles, hideNumbered, surface, pavedOnly, minRestaurants, minBikeShops, sortBy]);
  useEffect(() => { const t = setTimeout(loadList, 250); return () => clearTimeout(t); }, [loadList]);
  // While a harvest runs, refresh the list so trails appear as phase-1 writes
  // land — results render within seconds instead of after the whole import.
  useEffect(() => {
    const running = job && ["queued", "running"].includes(job.status);
    if (!running) return;
    const iv = setInterval(() => loadList(), 4000);
    return () => clearInterval(iv);
  }, [job, loadList]);
  useEffect(() => {
    if (state) console.info(`[TrailDiscovery] rendered ${trails.length} trail rows`);
  }, [trails, state]);
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);
  // Resume polling if a harvest is already running for the selected state.
  useEffect(() => {
    if (!state) return;
    pollStatus(state);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);
  useEffect(() => {
    if (job && ["queued", "running"].includes(job.status) && !pollRef.current) {
      setHarvesting(true);
      pollRef.current = setInterval(() => pollStatus(job.state), 3000);
    }
  }, [job, pollStatus]);

  const openTrail = async (id) => {
    setLoadingTrail(true); setSelected(null);
    try { const { data } = await api.get(`/admin/trails/${id}`); setSelected(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoadingTrail(false); }
  };

  const trailSave = useTrailSave(useCallback((result) => {
    setSelected((s) => ({ ...s, status: "saved", saved_bike_path_id: result?.bike_path_id }));
    loadStates(); loadList();
  }, [])); // eslint-disable-line react-hooks/exhaustive-deps
  const saving = trailSave.saving;
  const saveTrail = () => { if (selected) trailSave.save(selected.id, bufferMiles); };

  // Business Discovery — fetch once per (trail, corridor width), cache client-side.
  const loadBusinesses = useCallback(async (trailId, buf) => {
    const key = `${trailId}:${buf}`;
    if (bizCacheRef.current[key]) {
      const c = bizCacheRef.current[key];
      setBusinesses(c.businesses); setBizStatus(c.status); return;
    }
    setBizLoading(true); setBusinesses([]); setBizStatus(null);
    try {
      const { data } = await api.get(`/admin/trails/${trailId}/businesses`, { params: { buffer_miles: buf } });
      bizCacheRef.current[key] = { businesses: data.businesses || [], status: data.osm_status };
      setBusinesses(data.businesses || []); setBizStatus(data.osm_status);
    } catch (e) {
      setBizStatus("unavailable"); setBusinesses([]);
    } finally { setBizLoading(false); }
  }, []);

  useEffect(() => {
    if (selected?.id) loadBusinesses(selected.id, Number(bufferMiles) || 1);
    else { setBusinesses([]); setBizStatus(null); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id, bufferMiles]);

  const visibleBiz = useMemo(() => businesses.filter((b) => {
    if (!layers[b.layer]) return false;
    if (b.layer === "bike_shop") {
      if (bikeFilter === "partners") return b.partner;
      if (bikeFilter === "prospects") return !b.partner;
      if (bikeFilter === "non_contacted") return b.outreach_status === "NOT_CONTACTED";
    }
    return true;
  }), [businesses, layers, bikeFilter]);
  const bizCounts = useMemo(() => {
    const c = { restaurant: 0, bike_shop: 0, coffee: 0, brewery: 0, hotel: 0 };
    businesses.forEach((b) => { if (c[b.layer] != null) c[b.layer] += 1; });
    return c;
  }, [businesses]);
  const heatPoints = useMemo(
    () => businesses.filter((b) => b.lat != null && b.lng != null).map((b) => [b.lat, b.lng, 0.6]),
    [businesses]
  );

  // Manage a prospect shop straight from its map popup (P2 Outreach From Map).
  const handleShopAction = useCallback(async (shopId, action, note) => {
    if (action === "open_crm") {
      window.dispatchEvent(new CustomEvent("velopaths:open-shop", { detail: { shopId } }));
      return;
    }
    try {
      if (action === "partner") await api.post(`/admin/directory/shops/${shopId}/convert`, {});
      else await api.post(`/admin/directory/shops/${shopId}/quick-status`, { action, note: note || null });
      const patch = action === "partner"
        ? { partner: true, outreach_status: "ACTIVE_PARTNER" }
        : action === "contacted" ? { outreach_status: "CONTACTED" } : { partner: false };
      const apply = (arr) => arr.map((b) => (b.shop_id === shopId ? { ...b, ...patch } : b));
      setBusinesses((arr) => apply(arr));
      Object.keys(bizCacheRef.current).forEach((k) => {
        bizCacheRef.current[k] = { ...bizCacheRef.current[k], businesses: apply(bizCacheRef.current[k].businesses) };
      });
      toast.success(action === "partner" ? "Invited to partner" : action === "contacted" ? "Marked contacted" : "Marked as prospect");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }, []);

  const lines = selected?.lines_latlng || [];
  const bounds = lines.length ? lines.flat() : null;
  const jobRunning = job && ["queued", "running"].includes(job.status);

  return (
    <div className="space-y-4" data-testid="trail-discovery-panel">
      {/* Controls */}
      <div className="rounded-2xl border border-border bg-card p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-56">
            <div className="text-xs text-muted-foreground mb-1.5">State</div>
            <Select value={state || "none"} onValueChange={(v) => {
              const st = v === "none" ? "" : v;
              setState(st); setSelected(null); setCounty("all");
              if (st) {
                const p = readPrefs()[st];
                if (p) { setMinMiles(p.minMiles ?? "2"); setHideNumbered(p.hideNumbered ?? true); setSurface(p.surface ?? "all"); setPavedOnly(p.pavedOnly ?? false); setMinRestaurants(p.minRestaurants ?? "0"); setMinBikeShops(p.minBikeShops ?? "0"); setSortBy(p.sortBy ?? "length"); }
              }
            }}>
              <SelectTrigger data-testid="trail-state-select"><SelectValue placeholder="Select a state" /></SelectTrigger>
              <SelectContent className="max-h-80">
                <SelectItem value="none">Select a state</SelectItem>
                {stateOptions.map((s) => (
                  <SelectItem key={s.state} value={s.state}>{s.name}{s.available ? ` (${s.available})` : ""}{s.paved_long ? ` · ${s.paved_long} paved 10mi+` : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {countyOpts.length > 0 && (
            <div className="w-44">
              <div className="text-xs text-muted-foreground mb-1.5">County</div>
              <Select value={county} onValueChange={setCounty}>
                <SelectTrigger data-testid="trail-county-select"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-80">
                  <SelectItem value="all">All counties</SelectItem>
                  {countyOpts.map((c) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="relative flex-1 min-w-[220px]">
            <div className="text-xs text-muted-foreground mb-1.5">Search trails</div>
            <Search className="w-4 h-4 absolute left-3 top-[34px] text-muted-foreground" />
            <Input className="pl-9" placeholder="e.g. Suncoast, Legacy, Greenway" value={q} onChange={(e) => setQ(e.target.value)} disabled={!state} data-testid="trail-search-input" />
          </div>
          <div className="w-36">
            <div className="text-xs text-muted-foreground mb-1.5">Min length</div>
            <Select value={minMiles} onValueChange={setMinMiles}>
              <SelectTrigger data-testid="trail-minlength-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="0">All lengths</SelectItem>
                <SelectItem value="2">2+ miles</SelectItem>
                <SelectItem value="5">5+ miles</SelectItem>
                <SelectItem value="10">10+ miles</SelectItem>
                <SelectItem value="20">20+ miles</SelectItem>
                <SelectItem value="50">50+ miles</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="w-36">
            <div className="text-xs text-muted-foreground mb-1.5">Surface</div>
            <Select value={pavedOnly ? "paved" : surface} onValueChange={setSurface} disabled={pavedOnly}>
              <SelectTrigger data-testid="trail-surface-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All surfaces</SelectItem>
                <SelectItem value="paved">Paved</SelectItem>
                <SelectItem value="unpaved">Unpaved</SelectItem>
                <SelectItem value="mixed">Mixed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="w-36">
            <div className="text-xs text-muted-foreground mb-1.5">Min restaurants</div>
            <Select value={minRestaurants} onValueChange={setMinRestaurants}>
              <SelectTrigger data-testid="trail-min-restaurants-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="0">Any</SelectItem>
                <SelectItem value="5">5+</SelectItem>
                <SelectItem value="10">10+</SelectItem>
                <SelectItem value="25">25+</SelectItem>
                <SelectItem value="50">50+</SelectItem>
                <SelectItem value="100">100+</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="w-36">
            <div className="text-xs text-muted-foreground mb-1.5">Min bike shops</div>
            <Select value={minBikeShops} onValueChange={setMinBikeShops}>
              <SelectTrigger data-testid="trail-min-bikeshops-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="0">Any</SelectItem>
                <SelectItem value="1">1+</SelectItem>
                <SelectItem value="2">2+</SelectItem>
                <SelectItem value="5">5+</SelectItem>
                <SelectItem value="10">10+</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="w-36">
            <div className="text-xs text-muted-foreground mb-1.5">Sort by</div>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger data-testid="trail-sort-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="length">Length</SelectItem>
                <SelectItem value="restaurants">Restaurants</SelectItem>
                <SelectItem value="bike_shops">Bike shops</SelectItem>
                <SelectItem value="restaurants_per_mile">Restaurants / mile</SelectItem>
                <SelectItem value="bike_shops_per_mile">Bike shops / mile</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" onClick={harvest} disabled={!state || harvesting || jobRunning} data-testid="trail-harvest-btn">
            {(harvesting || jobRunning) ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : (current?.available ? <RefreshCw className="w-4 h-4 mr-2" /> : <DownloadCloud className="w-4 h-4 mr-2" />)}
            {current?.available ? "Re-harvest" : "Harvest state"}
          </Button>
          {current?.available > 0 && (
            <Button variant="ghost" onClick={recompute} disabled={recomputing} data-testid="trail-recompute-btn" title="Recompute CRM shop metrics after importing bike shops">
              {recomputing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}Recompute metrics
            </Button>
          )}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2">
          <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer" data-testid="trail-hide-numbered-toggle">
            <input type="checkbox" checked={hideNumbered} onChange={(e) => setHideNumbered(e.target.checked)} className="accent-primary w-3.5 h-3.5" />
            Hide numbered routes (show named destination trails only)
          </label>
          <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer" data-testid="trail-paved-only-toggle">
            <input type="checkbox" checked={pavedOnly} onChange={(e) => setPavedOnly(e.target.checked)} className="accent-primary w-3.5 h-3.5" />
            Paved only
          </label>
        </div>
        {jobRunning && (() => {
          const c = job.counts || {};
          const pct = c.trails_total ? Math.round(100 * (c.metrics_done ?? 0) / c.trails_total) : null;
          let eta = null;
          if (pct != null && pct > 0 && pct < 100 && job.started_at) {
            const el = (Date.now() - Date.parse(job.started_at)) / 1000;
            const rem = (el * (100 - pct)) / pct;
            eta = rem > 90 ? `${Math.round(rem / 60)}m` : `${Math.round(rem)}s`;
          }
          return (
            <div className="mt-3" data-testid="trail-harvest-status">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-amber-300">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                <span className="capitalize font-medium">{(job.phase || "").replace(/_/g, " ")}</span>
                {c.relations_named ? <span>· {c.relations_named} routes</span> : null}
                {c.trail_nodes ? <span>· {c.trail_nodes.toLocaleString()} nodes</span> : null}
                {c.trails_written != null ? <span>· {c.trails_written} imported</span> : null}
                {c.trails_skipped ? <span>· {c.trails_skipped} skipped</span> : null}
                {c.metrics_done != null ? <span>· metrics {c.metrics_done}/{c.trails_total ?? "?"}{pct != null ? ` (${pct}%)` : ""}</span> : null}
                {eta ? <span>· ~{eta} left</span> : null}
                <Button size="sm" variant="destructive" className="h-6 px-2 ml-1" onClick={abortHarvest} data-testid="trail-abort-btn">
                  <X className="w-3.5 h-3.5 mr-1" />Abort
                </Button>
              </div>
              {pct != null && (
                <div className="mt-1.5 h-1.5 rounded bg-amber-500/15 overflow-hidden">
                  <div className="h-full bg-amber-400 transition-all" style={{ width: `${pct}%` }} data-testid="trail-harvest-progress" />
                </div>
              )}
            </div>
          );
        })()}
        {job && job.status === "error" && (
          <div className="mt-3 flex items-start gap-2 text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg p-2.5" data-testid="trail-harvest-error">
            <span className="flex-1">{job.error || "Harvest failed."}</span>
            <Button size="sm" variant="outline" className="h-6 px-2 shrink-0" onClick={harvest} data-testid="trail-reharvest-btn">Re-harvest</Button>
            <Button size="sm" variant="ghost" className="h-6 px-2 shrink-0" onClick={() => setJob(null)} data-testid="trail-dismiss-error-btn">Dismiss</Button>
          </div>
        )}
        {state && current && !jobRunning && (
          <div className="mt-2 text-xs text-muted-foreground">
            <b className="text-foreground">{current.available}</b> trails available · <b className="text-emerald-300" data-testid="trail-state-paved-stat">{current.paved_long}</b> paved 10mi+ · <b className="text-emerald-400">{current.saved}</b> saved to Bike Paths
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-[360px_1fr] gap-4">
        {/* Trail list */}
        <div className="rounded-2xl border border-border bg-card overflow-hidden flex flex-col" style={{ maxHeight: 640 }}>
          <div className="px-4 py-2.5 border-b border-border text-xs text-muted-foreground">
            {loadingList ? "Loading…" : `${trails.length} trail${trails.length === 1 ? "" : "s"}`}
          </div>
          <div className="overflow-y-auto flex-1">
            {!state ? (
              <div className="p-8 text-center text-sm text-muted-foreground" data-testid="trail-list-empty">Select a state to browse its trails.</div>
            ) : trails.length === 0 && !loadingList ? (
              <div className="p-8 text-center text-sm text-muted-foreground" data-testid="trail-list-empty">
                No trails yet for {codeToName[state] || state}. Click <b>Harvest state</b> to load them from OpenStreetMap.
              </div>
            ) : trails.map((t) => (
              <button key={t.id} onClick={() => openTrail(t.id)} className={`w-full text-left px-4 py-3 border-b border-border/50 hover:bg-secondary/50 ${selected?.id === t.id ? "bg-secondary" : ""}`} data-testid={`trail-item-${t.id}`}>
                <div className="flex items-center gap-2">
                  <Route className="w-4 h-4 text-primary shrink-0" />
                  <span className="font-medium text-sm truncate">{t.name}</span>
                  {t.status === "saved" && <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" title="Saved" />}
                </div>
                <div className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-2 flex-wrap">
                  <span>{t.length_miles} mi</span>
                  {t.surface_class && t.surface_class !== "Unknown" && <Badge className={`border text-[10px] px-1.5 py-0 ${SURFACE_CHIP[t.surface_class] || "bg-muted text-muted-foreground border-border"}`}>{t.surface_class}</Badge>}
                  <span className="text-orange-300 flex items-center gap-0.5" data-testid={`trail-restaurants-${t.id}`}><Utensils className="w-3 h-3" />{t.restaurant_count ?? 0}</span>
                  <span className="text-cyan-300 flex items-center gap-0.5" data-testid={`trail-bikeshops-${t.id}`}><Bike className="w-3 h-3" />{t.market_bike_shop_count ?? 0}</span>
                  {t.total_shop_count ? <span className="text-emerald-300">CRM {t.total_shop_count}</span> : null}
                  {t.network && <Badge className="bg-muted text-muted-foreground border border-border text-[10px] px-1.5 py-0">{NET_LABEL[t.network] || t.network}</Badge>}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Map + preview */}
        <div className="rounded-2xl border border-border overflow-hidden relative" data-testid="trail-preview-map">
          <LeafletMap height={640}>
            {bounds && <FitBounds bounds={bounds} nonce={selected?.id} />}
            {lines.map((ln, i) => <Polyline key={i} positions={ln} pathOptions={{ color: "#0ea5e9", weight: 5 }} />)}
            {selected?.start_point && <CircleMarker center={[selected.start_point.lat, selected.start_point.lng]} radius={7} pathOptions={{ color: "#fff", weight: 2, fillColor: "#22c55e", fillOpacity: 1 }}><Tooltip>Start</Tooltip></CircleMarker>}
            {selected?.end_point && <CircleMarker center={[selected.end_point.lat, selected.end_point.lng]} radius={7} pathOptions={{ color: "#fff", weight: 2, fillColor: "#ef4444", fillOpacity: 1 }}><Tooltip>End</Tooltip></CircleMarker>}
            {selected && visibleBiz.length > 0 && <BusinessLayers businesses={visibleBiz} onShopAction={handleShopAction} />}
            {selected && heatOn && <HeatLayer points={heatPoints} enabled={heatOn} />}
          </LeafletMap>

          {loadingTrail && <div className="absolute inset-0 z-[1000] bg-background/60 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>}

          {selected && (
            <div className="absolute bottom-3 left-3 z-[1000] flex flex-col gap-2 max-w-[calc(100%-1.5rem)]" data-testid="business-layer-toggles">
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(LAYER_META).map(([k, m]) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setLayers((l) => ({ ...l, [k]: !l[k] }))}
                    data-testid={`biz-toggle-${k}`}
                    className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] backdrop-blur transition-colors ${layers[k] ? "bg-card/95 border-border text-foreground" : "bg-card/60 border-border/50 text-muted-foreground"}`}
                  >
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: m.color, opacity: layers[k] ? 1 : 0.35 }} />
                    {m.label}<span className="opacity-60">{bizCounts[k] || 0}</span>
                  </button>
                ))}
                <button type="button" onClick={() => setHeatOn((v) => !v)} data-testid="biz-toggle-heat"
                  className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] backdrop-blur transition-colors ${heatOn ? "bg-card/95 border-border text-foreground" : "bg-card/60 border-border/50 text-muted-foreground"}`}>
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: "linear-gradient(90deg,#22d3ee,#facc15,#ef4444)", opacity: heatOn ? 1 : 0.4 }} />
                  Heatmap
                </button>
              </div>
              {layers.bike_shop && (
                <div className="flex items-center gap-1.5" data-testid="bike-shop-filter">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: LAYER_META.bike_shop.color }} />
                  <Select value={bikeFilter} onValueChange={setBikeFilter}>
                    <SelectTrigger className="h-7 text-[11px] w-40 bg-card/95 backdrop-blur" data-testid="bike-shop-filter-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All bike shops</SelectItem>
                      <SelectItem value="partners">Partners only</SelectItem>
                      <SelectItem value="prospects">Prospects only</SelectItem>
                      <SelectItem value="non_contacted">Non-contacted</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              {bizLoading && (
                <div className="w-fit flex items-center gap-1.5 rounded-md border border-border bg-card/90 px-2 py-1 text-[11px]" data-testid="biz-loading">
                  <Loader2 className="w-3 h-3 animate-spin" />Loading nearby businesses…
                </div>
              )}
              {!bizLoading && bizStatus === "unavailable" && (
                <div className="w-fit rounded-md border border-amber-500/30 bg-amber-500/15 px-2 py-1 text-[11px] text-amber-300" data-testid="biz-unavailable">
                  Business data temporarily unavailable
                </div>
              )}
            </div>
          )}

          {selected && (
            <div className="absolute top-3 left-3 right-3 z-[1000] rounded-xl border border-border bg-card/95 backdrop-blur p-4 shadow-lg max-w-md" data-testid="trail-preview-card">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-heading font-bold text-base">{selected.name}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">OSM · {selected.source_id} · ODbL</div>
                </div>
                {selected.status === "saved" && <Badge className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">Saved</Badge>}
              </div>
              <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
                <div className="flex items-center gap-1.5"><Ruler className="w-3.5 h-3.5 text-primary" />{selected.length_miles} miles</div>
                <div className="flex items-center gap-1.5"><Layers className="w-3.5 h-3.5 text-primary" />{selected.surface_class || "Unknown"}{selected.surface_type && selected.surface_type !== "unknown" ? ` (${selected.surface_type})` : ""}</div>
                <div className="col-span-2 mt-1 grid grid-cols-2 gap-2">
                  <div className="rounded-lg border border-orange-500/30 bg-orange-500/5 p-2" data-testid="trail-detail-restaurants">
                    <div className="text-lg font-heading font-extrabold text-orange-300 flex items-center gap-1"><Utensils className="w-4 h-4" />{selected.restaurant_count ?? 0}</div>
                    <div className="text-[10px] text-muted-foreground">restaurants nearby · {selected.restaurants_per_mile ?? 0}/mi</div>
                  </div>
                  <div className="rounded-lg border border-cyan-500/30 bg-cyan-500/5 p-2" data-testid="trail-detail-bikeshops">
                    <div className="text-lg font-heading font-extrabold text-cyan-300 flex items-center gap-1"><Bike className="w-4 h-4" />{selected.market_bike_shop_count ?? 0}</div>
                    <div className="text-[10px] text-muted-foreground">bike shops nearby (market) · {selected.market_bike_shops_per_mile ?? 0}/mi</div>
                  </div>
                </div>
                <div className="col-span-2 flex items-center gap-4 text-[11px]">
                  <span><b className="text-emerald-300">{selected.partner_shop_count ?? 0}</b> partner shops</span>
                  <span><b className="text-amber-300">{selected.prospect_shop_count ?? 0}</b> prospect shops</span>
                  <span><b className="text-foreground">{selected.total_shop_count ?? 0}</b> CRM total</span>
                </div>
                <div className="col-span-2 rounded-lg border border-violet-500/30 bg-violet-500/5 p-2 text-[11px]" data-testid="trail-uncaptured">
                  <b className="text-violet-300 text-sm">{Math.max(0, (selected.market_bike_shop_count || 0) - (selected.total_shop_count || 0))}</b> uncaptured market opportunity <span className="text-muted-foreground">(market shops not yet managed)</span>
                </div>
                <div className="col-span-2 flex items-center gap-4 text-[11px]">
                  <span>Market penetration: <b className="text-foreground">{selected.market_bike_shop_count ? Math.round(100 * (selected.total_shop_count || 0) / selected.market_bike_shop_count) + "%" : "—"}</b></span>
                </div>
                <div className="col-span-2"><span className="text-muted-foreground">Cities:</span> {selected.cities?.length ? selected.cities.join(", ") : "—"}</div>
                <div className="col-span-2"><span className="text-muted-foreground">Counties:</span> {selected.counties?.length ? selected.counties.join(", ") : "—"}</div>
                <div className="col-span-2 flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-primary" />{selected.trailheads?.length || 0} trailheads</div>
                {selected.metrics_last_computed && <div className="col-span-2 text-[10px] text-muted-foreground">Metrics updated {new Date(selected.metrics_last_computed).toLocaleDateString()}</div>}
              </div>
              {selected.status !== "saved" && (
                <div className="flex items-center gap-2 mt-3" data-testid="trail-buffer-row">
                  <span className="text-[11px] text-muted-foreground whitespace-nowrap">Corridor width</span>
                  <Select value={bufferMiles} onValueChange={setBufferMiles}>
                    <SelectTrigger className="h-8 text-xs w-32" data-testid="trail-buffer-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0.5">½ mile</SelectItem>
                      <SelectItem value="1">1 mile</SelectItem>
                      <SelectItem value="1.5">1.5 miles</SelectItem>
                      <SelectItem value="2">2 miles</SelectItem>
                      <SelectItem value="3">3 miles</SelectItem>
                      <SelectItem value="5">5 miles</SelectItem>
                    </SelectContent>
                  </Select>
                  <span className="text-[11px] text-muted-foreground">buffer around the trail</span>
                </div>
              )}
              <div className="flex gap-2 mt-3">
                {selected.status === "saved" ? (
                  <Button className="flex-1" variant="outline" disabled data-testid="trail-saved-btn"><CheckCircle2 className="w-4 h-4 mr-2" />Saved as draft</Button>
                ) : (
                  <Button className="flex-1" onClick={saveTrail} disabled={saving} data-testid="trail-save-btn">
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Save to Bike Paths (draft)
                  </Button>
                )}
                <Button variant="ghost" onClick={() => setSelected(null)} data-testid="trail-cancel-btn">Cancel</Button>
              </div>
              <TrailSaveProgress job={trailSave.job} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
